import i18n from 'i18n';
import _ from 'lodash';
import { getByteLength } from './StringUtil';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';

export function validateEmpty(value) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && _.isEmpty(value)) ||
    (Array.isArray(value) && _.isEmpty(value))
  ) {
    return i18n.t<string>('formValidate.labels.emptyError');
  }
}

export function validateStringMaxLength(value: string, maxLength: number) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }
  const stringLength = getByteLength(value);
  if (stringLength > maxLength) {
    return i18n.t<string>('formValidate.labels.remainingCharacters', {
      num: maxLength - stringLength,
    });
  }
}

export function validateMinimum(
  value,
  minValue,
  i18nKey = 'formValidate.labels.minimumError',
  currency = '',
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber < minValue) {
    let min = currency + minValue;
    return i18n.t<string>(i18nKey, { min });
  }
}

export function validateMinMax(value, min, max) {
  let error = validateMinimum(value, min);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber > max) {
    return i18n.t<string>('formValidate.labels.maximumError', { max });
  }
}

export function validateMax(
  value,
  max,
  currency?: string,
  i18nKey = 'formValidate.labels.maximumError',
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber > max) {
    return i18n.t<string>(i18nKey, {
      max: currency ? formatPriceWithCurrency(currency, max) : max,
    });
  }
}

export function validateUrl(url: string): string | undefined {
  const error = validateEmpty(url);
  if (error) return error;

  const urlExpression =
    /(^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|^https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/;
  const urlMomoshopRegex =
    /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momoshop\.com\.tw(?:\/.+)?$/;
  const urlMomoRegex = /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momo\.dm(?:\/.+)?$/;
  const urlTPRegex =
    /^https?:\/\/(?:[a-zA-Z0-9]+\.)?momoshop\.com\.tw\/TP(?:\/.+)?$/;
  const urlMobileTPRegex = /^https?:\/\/m\.momoshop\.com\.tw\/TP(?:\/.+)?$/;
  const cnRegex = /cn=([0-9]{10,})(?=&|\b)/;
  const url3pMainRegex = /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/main/;
  const url3pGoodsRegex = /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/goods/;
  const url3pCategoryRegex =
    /^https:\/\/m\.momoshop\.com\.tw\/category\.momo\?cn=[^&]+&entpCode=[^&]+/;
  const url3pGoodsDetailRegex =
    /^https:\/\/www\.momoshop\.com\.tw\/TP\/[^/]+\/goodsDetail\/[^/]+/;

  if (!urlExpression.test(url)) {
    return i18n.t<string>('formValidate.labels.needUrl');
  }
  if (!urlMomoRegex.test(url) && !urlMomoshopRegex.test(url)) {
    return i18n.t<string>('creativeSetupFlow.labels.urlErrorHint');
  }
  if (url3pCategoryRegex.test(url) && !cnRegex.test(url)) {
    return '請填寫符合分類頁提示的網址';
  }
  if (
    (urlTPRegex.test(url) || urlMobileTPRegex.test(url)) &&
    !url3pMainRegex.test(url) &&
    !url3pGoodsRegex.test(url) &&
    !url3pGoodsDetailRegex.test(url)
  ) {
    return '不支援您所輸入的 「TP」 網址，請參考下方 mo店 + 連結網址說明';
  }
}

export function validateEmail(email: string): string | undefined {
  if (_.isEmpty(email)) {
    return i18n.t<string>('formValidate.labels.emailRequired');
  }
  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
    return i18n.t<string>('formValidate.labels.invalidEmail');
  }
  return undefined;
}

export function validateInteger(value) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isInteger(valueNumber)) {
    return i18n.t<string>('formValidate.labels.integerError');
  }
}
